<script lang="ts">
	//Hooks
	import { beforeUpdate } from 'svelte';
	//Services
	import { localReq, postReq } from '../../../../services/http';
	import { format } from '../../../../services/i18n';
	//Stores
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { real_id } from '../../../../helpers/interface_helpers';
	//Helpers
	import { get_local_ip } from '../../../../helpers/interface_helpers';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	//Design system components
	import { Alert, Button, Checkbox, Text, toast, Radio, Icon } from '@Shelly/shelly-design-system';
	//Components
	import ModalPrompt from '../../../../components/ModalPrompt.svelte';

	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	export let device;

	const calibration_options = [
		$format('alert_leading_edge_heading', { default: 'Leading edge' }),
		$format('alert_trailing_edge_heading', { default: "'Trailing edge'" }),
	];

	let showModal = false;

	let settings = readDeviceSettings(device.id);
	$: settingsStore = getDeviceSettings(device.id);
	$: settings = $settingsStore ?? settings;

	let selected = settings.pulse_mode - 1; //here i will lower pulse_mode index because local pulse mode is with values 1 or 2 :D
	let cacheSelected = selected;

	let bypass = settings.bypass;

	let cacheBypass = bypass;

	let loadAuto = settings.load_autodetect === 1;

	let cacheAuto = loadAuto;

	$: calibrated = settings.calibrated;

	let localIp = get_local_ip(device.id),
		props,
		loading = false,
		calibrateLoading = false,
		calibrationButton;

	beforeUpdate(() => {
		calibrationButton = calibrated
			? $format('button_delete_calibration')
			: $format('button_start_calibration');

		props = {
			//this check is needed here because otherwise modal doesn't change its text
			// TODO add translation label_confirmation
			title: calibrated
				? `${$format('button_delete')} ${$format('sub_settings_title_calibration')}?`
				: `${$format('button_calibrate')} ${$format('process_device')}?`,
			button_1: calibrated ? $format('button_delete') : $format('button_calibrate'),
			callback: calibrate,
			button_2: $format('button_cancel'),
			button_1_variant: calibrated ? 'danger' : 'primary',
		};
	});

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({ onClick: saveSettings, loading, disabled: loading });
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});

	const calibrate = async () => {
		//this function calibrates or removes devices calibration
		let res;
		calibrateLoading = true;
		if (localIp) {
			res = await localReq(device, `${localIp}/settings/`, 'post', {
				calibrate: calibrated ? 0 : 1,
			});
		} else {
			res = await postReq(`/device/${device.category}/settings/calibration`, {
				enabled: calibrated ? 0 : 1,
				channel: device.channel,
				id: device.id,
			});
		}

		if (res.isok) {
			toast.open({
				type: 'success',
				title: calibrated
					? $format('calibration_deleted_scc_toast')
					: $format('calibration_started_scc_toast') ||
						`Device calibration ${calibrated ? 'removed' : 'started'}.`,
			});
			settings.calibrated = calibrated;
			devices_settings.update_devices_settings(device.id, settings);
			calibrateLoading = false;
		} else {
			toast.open({ type: 'danger', title: $format('error_global') });
			calibrateLoading = false;
		}
	};

	const setDimmingType = (e) => {
		selected = calibration_options.indexOf(e.detail);
	};

	const setBypass = () => {
		bypass = !bypass;
	};

	const setAuto = () => {
		loadAuto = !loadAuto;
	};

	const saveSettings = async () => {
		let saveDimmingType;
		let saveBypass;
		let saveAuto;
		if (cacheSelected !== selected) {
			loading = true;
			if (localIp) {
				saveDimmingType = await localReq(device, `${localIp}/settings/`, 'post', {
					pulse_mode: selected + 1, //because device accepts 1 and 2 as values
				});
			} else {
				saveDimmingType = await postReq(`/device/${device.category}/settings/pulse_mode`, {
					pulse_mode: selected + 1, //because device accepts 1 and 2 as values
					channel: device.channel,
					id: device.id,
				});
			}
		}
		if (cacheBypass !== bypass) {
			loading = true;
			if (localIp) {
				saveBypass = await localReq(device, `${localIp}/settings/`, 'post', {
					bypass: bypass,
				});
			} else {
				saveBypass = await postReq(`device/generic_command`, {
					id: device.id,
					command: 'bypass',
					payload: JSON.stringify({
						id: device.channel,
						enabled: bypass ? 1 : 0,
					}),
				});
			}
		}
		if (cacheAuto !== loadAuto) {
			loading = true;
			if (localIp) {
				saveAuto = await localReq(device, `${localIp}/settings/`, 'post', {
					load_autodetect: loadAuto,
				});
			} else {
				saveAuto = await postReq(`device/generic_command`, {
					id: device.id,
					command: 'load_autodetect',
					payload: JSON.stringify({
						id: device.channel,
						autodetect: loadAuto ? 1 : 0,
					}),
				});
			}
		}
		Promise.all([saveDimmingType, saveBypass, saveAuto]).then((res) => {
			if (typeof res[0] !== 'undefined') {
				if (res[0].isok) {
					settings.pulse_mode = selected;
					devices_settings.update_devices_settings(device.id, settings);
					toast.open({
						type: 'success',
						title:
							$format('dimming_type_scc_toast', {
								values: { type: calibration_options[selected] },
							}) || `Dimming type is changed to ${calibration_options[selected]}.`,
					});
					// selected = selected - 1;
					cacheSelected = selected;
				} else {
					selected = cacheSelected;
					toast.open({ type: 'danger', title: $format('error_global') });
				}
			}
			if (typeof res[1] !== 'undefined') {
				if (res[1].isok) {
					settings.bypass = bypass;
					devices_settings.update_devices_settings(device.id, settings);
					toast.open({
						type: 'success',
						title: bypass
							? $format('bypass_calibration_enabled_scc_toast')
							: $format('bypass_calibration_disabled_scc_toast') ||
								`Bypass calibration was ${bypass ? 'enabled' : 'disabled'}.`,
					});
					cacheBypass = bypass;
				} else {
					bypass = cacheBypass;
					toast.open({ type: 'danger', title: $format('error_global') });
				}
			}
			if (typeof res[2] !== 'undefined') {
				if (res[2].isok) {
					settings.load_autodetect = loadAuto ? 1 : 0;
					devices_settings.update_devices_settings(device.id, settings);
					toast.open({
						type: 'success',
						title: loadAuto
							? $format('auto_detect_calibration_enabled_scc_toast')
							: $format('auto_detect_calibration_disabled_scc_toast') ||
								`Auto detect while calibration was ${loadAuto ? 'enabled' : 'disabled'}.`,
					});
					cacheAuto = loadAuto;
				} else {
					loadAuto = cacheAuto;
					toast.open({ type: 'danger', title: $format('error_global') });
				}
			}
			loading = false;
		});
	};
</script>

<Text element="h2" class="mb-s">
	{$format('heading_choose_dimming_type', {
		default: 'Choose your dimming type:',
	})}
</Text>
<div class="mb-m">
	{#each calibration_options as option}
		<Radio
			value={option}
			size="m"
			selected={calibration_options[selected]}
			on:click={(e) => {
				setDimmingType({ detail: e.target.value });
			}}
			id="radio-01dyyy4"
		>
			{option}
		</Radio>
		{#if calibration_options[selected] === calibration_options[0] && option === calibration_options[0]}
			<Alert
				type="warning"
				isHeadingBold
				heading={$format('alert_leading_edge_heading', {
					default: 'Leading edge',
				})}
				class="mb-s mt-s"
				message={$format('alert_dimming_type_message', {
					default:
						"Recommended only if this type of dimming is indicated on the manufacturer's packaging and for consumers up to 100W.",
				})}
			/>
		{/if}
	{/each}
</div>

<Checkbox
	size="m"
	class={classMap({ 'mb-s': !loadAuto, 'mb-m': loadAuto })}
	checked={loadAuto}
	on:click={setAuto}
	id="checkbox-5g5dm44"
>
	{$format('auto_detect_during_calibration', {
		default: 'Auto detect type of load during calibration.',
	})}
</Checkbox>

{#if !loadAuto}
	<Alert
		type="warning"
		isHeadingBold
		heading={$format('alert_auto_detect_heading', {
			default: 'Auto detect type of load',
		})}
		class="mb-m"
		message={$format('alert_dimming_improper_operation_message', {
			default:
				'Improper operation mode selection can lead to overheating and damage to the device and the associated loads.',
		})}
	/>
{/if}

<Checkbox size="m" class="mb-s" checked={bypass} on:click={setBypass} id="checkbox-fp0sd66">
	{$format('label_calibrate_with_bypass', {
		default: 'Calibrate with Bypass',
	})}
</Checkbox>

<Alert
	type="info"
	isHeadingBold
	class="mb-m"
	heading={$format('alert_bypass_calibration_heading', {
		default: 'Bypass calibration',
	})}
	message={$format('alert_bypass_calibration_message', {
		default:
			'Could be chosen only if the Bypass is connected, but some bulbs can be calibrated better with Bypass even then if option is not set.',
	})}
/>

<Button
	loading={calibrateLoading}
	variant="secondary"
	size="m"
	width="full"
	class="mb-s"
	on:click={() => (showModal = true)}>{calibrationButton}</Button
>

{#if !$showNewLayout}
	<Button {loading} size="m" width="full" on:click={saveSettings}>
		{$format('button_save')}
	</Button>
{/if}

{#if showModal}
	<ModalPrompt bind:showModal {props}>
		{#if calibrated}
			<div class="d-flex flex-direction-column align-items-center">
				<Icon icon="text-danger fa-light fa-trash" size="xxl" />
				<Text class="mt-xs width-full">
					{$format('remove_calibration_msg', {
						default:
							'This action will remove the current calibration settings from your device, which may affect its performance.',
					})}
				</Text>
			</div>
		{:else}
			<Text>
				{$format('calibration_msg', {
					default:
						'Calibration ensures accurate performance by adjusting the device’s settings.',
				})}
			</Text>

			<Alert
				class="mt-m"
				type="warning"
				heading={$format('calibration_process_alert', {
					default:
						'Note that during or after the process may temporarily disconnect or change behavior.',
				})}
			/>
		{/if}
	</ModalPrompt>
{/if}
